"use client"
import { useState, useEffect, useContext } from "react"
import {
  Box,
  TextField,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  Typography,
  InputAdornment,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Divider,
  useTheme,
  alpha,
  TablePagination,
  CircularProgress,
  Alert,
  Fade,
} from "@mui/material"
import Autocomplete from "@mui/material/Autocomplete"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import SearchIcon from "@mui/icons-material/Search"
import PersonIcon from "@mui/icons-material/Person"
import EmailIcon from "@mui/icons-material/Email"
import PhoneIcon from "@mui/icons-material/Phone"
import BadgeIcon from "@mui/icons-material/Badge"
import RefreshIcon from "@mui/icons-material/Refresh"
import { Visibility, VisibilityOff } from "@mui/icons-material"
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount"
import { ErpContext } from "../../context/ErpContext.js"
import ModalEditarUsuario from "./ModalEditarUsuario.js"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"

export default function TablaUsuariosComponent() {
  const theme = useTheme()
  const { getUsuario, usuarios, getRolUsuario, deleteUsuarioSoft, isNavbarCollapsed } = useContext(ErpContext)

  const [filteredUsuarios, setFilteredUsuarios] = useState([])
  const [searchText, setSearchText] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [roles, setRoles] = useState([])
  const [rolFiltro, setRolFiltro] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState({ open: false, usuario: null })

  const [showPasswordForId, setShowPasswordForId] = useState(null)
  const [openModalEditarUsuario, setOpenModalEditarUsuario] = useState(false)
  const [usuarioEditar, setUsuarioEditar] = useState({})

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  useEffect(() => {
    fetchUsuarios()
  }, [])

  const fetchUsuarios = async () => {
    setLoading(true)
    setError(null)
    try {
      const [_, rolesResp] = await Promise.all([
        getUsuario(),
        getRolUsuario(),
      ])
      setRoles(rolesResp || [])
      setFilteredUsuarios(usuarios || [])
    } catch (err) {
      console.error("Error al cargar usuarios:", err)
      setError("No se pudieron cargar los usuarios. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const text = searchText.trim().toLowerCase()
    const filtered = (usuarios || []).filter((user) => {
      const matchText = !text ||
        user.usuario?.toLowerCase().includes(text) ||
        user.nombre?.toLowerCase().includes(text) ||
        user.apellido?.toLowerCase().includes(text) ||
        user.dniCuitCuil?.toLowerCase().includes(text) ||
        user.email?.toLowerCase().includes(text)
      const matchRol = !rolFiltro || user.rol === rolFiltro.id
      return matchText && matchRol
    })
    setFilteredUsuarios(filtered)
    setPage(0)
  }, [searchText, rolFiltro, usuarios])

  const handleTogglePasswordVisibility = (id) => {
    setShowPasswordForId((prevId) => (prevId === id ? null : id))
  }

  const handleCerrarModalCrearUsuario = async (refresh = false) => {
    setOpenModalEditarUsuario(false)
    if (refresh) {
      await fetchUsuarios()
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    }
  }

  const editarUsuario = (usuario) => {
    // console.log("Usuario a editar:", usuario)
    setUsuarioEditar(usuario)
    setOpenModalEditarUsuario(true)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  const paginatedUsuarios = filteredUsuarios.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, overflow: "hidden" }}>
      <Card
        elevation={2}
        sx={{
          mb: 3,
          borderRadius: 2,
          overflow: "visible",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <SupervisorAccountIcon
              sx={{
                fontSize: 28,
                color: "#5b5b5f",
                mr: 1.5,
              }}
            />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: "#5b5b5f" }}>
              Gestión de Usuarios
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.2fr 0.8fr auto" },
            gap: 2,
            alignItems: "center",
          }}>
            <TextField
              placeholder="Buscar por usuario, nombre, apellido, DNI o email..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Autocomplete
              size="small"
              options={roles}
              getOptionLabel={(option) => option.descripcion || ""}
              value={rolFiltro}
              onChange={(e, val) => setRolFiltro(val)}
              renderInput={(params) => (
                <TextField {...params} placeholder="Filtrar por rol" />
              )}
            />

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={fetchUsuarios}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  minWidth: 100,
                  borderColor: "#5b5b5f",
                  color: "#5b5b5f",
                  "&:hover": {
                    borderColor: "#5b5b5f",
                    backgroundColor: "rgba(91, 91, 95, 0.04)",
                  },
                }}
              >
                Actualizar
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Fade in={success}>
        <Alert
          severity="success"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: 1,
          }}
          onClose={() => setSuccess(false)}
        >
          Operación completada con éxito
        </Alert>
      </Fade>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: 1,
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Card
        elevation={2}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          height: "auto",
          width: "100%",
        }}
      >
        <Box sx={{ position: "relative" }}>
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <TableContainer
            sx={{
              maxHeight: "calc(100vh - 300px)",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "2px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
              },
            }}
          >
            <Table stickyHeader sx={{ tableLayout: "fixed", minWidth: 1000 }}>
              <TableHead>
                <TableRow sx={{ height: "45px" }}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "5%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "12%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Usuario
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "15%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Contraseña
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "15%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Nombre Completo
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "12%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    DNI/CUIT/CUIL
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "16%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Contacto
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "10%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Rol
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "9%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Ubicación
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "12%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsuarios.length > 0 ? (
                  paginatedUsuarios.map((user) => (
                    <TableRow
                      key={user.id}
                      hover
                      sx={{
                        height: "35px",
                        "&:nth-of-type(even)": {
                          backgroundColor: "#F7F7F7",
                        },
                        "&:hover": {
                          backgroundColor: "#E3EAFD",
                          transition: "background 0.3s ease",
                        },
                        transition: "background-color 0.2s",
                      }}
                    >
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Chip
                          label={user.id}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 500,
                            minWidth: 40,
                            justifyContent: "center",
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <PersonIcon fontSize="small" sx={{ color: "#5b5b5f", mr: 1 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {user.usuario || "—"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="body2">
                            {showPasswordForId === user.id ? user.password : "••••••••"}
                          </Typography>
                          <IconButton
                            onClick={() => handleTogglePasswordVisibility(user.id)}
                            size="small"
                            sx={{
                              backgroundColor: "rgba(91, 91, 95, 0.1)",
                              color: "#5b5b5f",
                              "&:hover": {
                                backgroundColor: "rgba(91, 91, 95, 0.2)",
                              },
                            }}
                          >
                            {showPasswordForId === user.id ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Typography variant="body2">
                          {`${user.nombre || ""} ${user.apellido || ""}`.trim() || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <BadgeIcon fontSize="small" sx={{ color: "#5b5b5f", mr: 1 }} />
                          <Typography variant="body2">{user.dniCuitCuil || "—"}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {user.email && (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <EmailIcon fontSize="small" sx={{ color: "#5b5b5f", mr: 1 }} />
                              <Typography variant="body2" noWrap>
                                {user.email}
                              </Typography>
                            </Box>
                          )}
                          {user.telefono && (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <PhoneIcon fontSize="small" sx={{ color: "#5b5b5f", mr: 1 }} />
                              <Typography variant="body2">{user.telefono}</Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Typography variant="body2">
                          {roles.find(r => r.id === user.rol)?.descripcion || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Tooltip
                          title={`${user.direccion || ""} ${user.localidad || ""} ${user.provincia || ""}`}
                          arrow
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 120,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {user.localidad ? `${user.localidad}, ${user.provincia || ""}` : "—"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Tooltip title="Editar usuario">
                          <IconButton
                            onClick={() => editarUsuario(user)}
                            size="small"
                            sx={{
                              backgroundColor: "rgba(91, 91, 95, 0.1)",
                              color: "#5b5b5f",
                              "&:hover": {
                                backgroundColor: "rgba(91, 91, 95, 0.2)",
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar usuario">
                          <IconButton
                            onClick={() => setConfirmDelete({ open: true, usuario: user })}
                            size="small"
                            sx={{
                              ml: 1,
                              backgroundColor: "rgba(211, 47, 47, 0.1)",
                              color: "#d32f2f",
                              "&:hover": {
                                backgroundColor: "rgba(211, 47, 47, 0.2)",
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      {loading ? (
                        <Typography color="text.secondary">Cargando usuarios...</Typography>
                      ) : searchText ? (
                        <Typography color="text.secondary">
                          No se encontraron usuarios con ese criterio de búsqueda
                        </Typography>
                      ) : (
                        <Typography color="text.secondary">No hay usuarios registrados</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredUsuarios.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          />
        </Box>
      </Card>

      <ModalEditarUsuario
        open={openModalEditarUsuario}
        handleClose={handleCerrarModalCrearUsuario}
        setOpen={setOpenModalEditarUsuario}
        usuario={usuarioEditar}
      />

      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, usuario: null })}>
        <DialogTitle>Eliminar usuario</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Vas a eliminar al usuario <strong>{confirmDelete.usuario?.usuario}</strong>. Esta acción es un borrado lógico (eliminado = true).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, usuario: null })}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={async () => {
            try {
              await deleteUsuarioSoft(confirmDelete.usuario.id)
              await fetchUsuarios()
              setSuccess(true)
              setTimeout(() => setSuccess(false), 3000)
            } catch (e) {
              setError("No se pudo eliminar el usuario")
            } finally {
              setConfirmDelete({ open: false, usuario: null })
            }
          }}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
